import 'react-mobile-datepicker-ts/dist/main.css'
import DatePicker, {DateConfig} from "react-mobile-datepicker-ts";
import "./styles.css";
import {Button, Modal} from "@telegram-apps/telegram-ui";
import {TbCheck, TbX} from "react-icons/tb";
import {useEffect, useState} from "react";
import {
  ModalHeader
} from "@telegram-apps/telegram-ui/dist/components/Overlays/Modal/components/ModalHeader/ModalHeader";
import {useTranslation} from "react-i18next";
import {disableVerticalSwipes, enableVerticalSwipes, mainButton, secondaryButton} from "@telegram-apps/sdk-react";
import {setOnClickMainButton, setOnClickSecondaryButton} from "@/helpers/tgButtons.ts";
import {haptic} from "@/helpers/haptic.ts";

const defaultDate = new Date(2000, 6, 15)

export const ModalDatePicker = (
  {
    opened = false,
    initialDate,
    onApply,
    onCancel,
    maxDate = new Date(),
    title = '',
  }: {
    opened: boolean,
    initialDate?: Date | undefined | null,
    onApply: (date: Date) => Promise<void>,
    onCancel: () => void,
    maxDate?: Date,
    title?: string,
  }
) => {
  const [ currentDate, setCurrentDate ] = useState<Date>(initialDate || defaultDate);
  const [ isSaveInProgress, setIsSaveInProgress ] = useState<boolean>(false);
  const { t, i18n: {language} } = useTranslation();

  if (!title) {
    title = t('profile.birthday')
  }

  useEffect(() => {
    if (opened) {
      disableVerticalSwipes.ifAvailable();
    } else {
      enableVerticalSwipes.ifAvailable();
    }

    return () => {
      enableVerticalSwipes.ifAvailable();
    };
  }, [opened]);

  useEffect(() => {
    if (!opened) {
      mainButton.setParams({
        isVisible: false,
      });
      if (secondaryButton.isSupported()) {
        secondaryButton.setParams({
          isVisible: false,
        });
      }
      return;
    }
    mainButton.setParams({
      isVisible: true,
      isLoaderVisible: isSaveInProgress,
      isEnabled: !isSaveInProgress,
      text: t('action.apply'),
    });

    if (secondaryButton.isSupported()) {
      secondaryButton.setParams({
        isVisible: true,
        isEnabled: !isSaveInProgress,
        text: t('action.cancel'),
      });
    }
  }, [isSaveInProgress, opened]);

  useEffect(() => {
    setOnClickSecondaryButton(() => {
      onCancel();
    })
    return () => {
      mainButton.setParams({
        isVisible: false,
      });
      if (secondaryButton.isSupported()) {
        secondaryButton.setParams({
          isVisible: false,
        });
      }
    }
  }, []);

  const getMonthName = (date: Date) => {
    return date.toLocaleString(language, { month: 'short' });
  }

  const dateConfig: DateConfig[] = [
    {
      format: "YYYY",
      caption: "Year",
      step: 1,
      type: "year"
    },
    {
      format: getMonthName,
      caption: "Mon",
      step: 1,
      type: "month"
    },
    {
      format: "DD",
      caption: "Day",
      step: 1,
      type: "date"
    },
  ];

  const onSave = async () => {
    setIsSaveInProgress(true);
    if (onApply) {
      await onApply(currentDate);
    }
    setIsSaveInProgress(false);
  }

  useEffect(() => {
    setOnClickMainButton(async () => {
      await onSave();
    })
  }, [onSave]);

  const onChange = (selectedDate: Date) => {
    haptic.selection();
    setCurrentDate(selectedDate);
  }

  return <Modal className={"bg-secondary"}
                header={<ModalHeader>{title}</ModalHeader>}
    open={opened} dismissible={false}>
    <DatePicker isPopup={false}
                value={currentDate}
                min={new Date(1900, 0, 1)}
                max={maxDate}
                showHeader={false}
                showFooter={false}
                dateConfig={dateConfig}
                onChange={onChange}
    />
    {import.meta.env.MODE === 'development2' && <div className={"mr-3 ml-3"}>
      <Button stretched={true}
              before={<TbCheck size={20}/>}
              disabled={isSaveInProgress}
              loading={isSaveInProgress}
              onClick={onSave}
              mode={"filled"}>
        {t('action.save')}
      </Button>
      <Button stretched={true}
              before={<TbX size={20}/>}
              disabled={isSaveInProgress}
              onClick={() => {
                onCancel();
              }}
              mode={"plain"}>
        {t('action.cancel')}
      </Button>
    </div>}
  </Modal>
}
